import { Dispatch } from 'redux';
import BaseAction from 'src/models/common/BaseAction';
import UserApiController from 'src/api/UserApiController';
import { Checkpoints } from "src/constants/Checkpoints";
import { Planets } from "src/constants/Planets";
import { ReduxStoreModel } from "src/redux/ReduxModel";
import FullCheckpointStatusDTO from "src/models/frontend/redux/FullCheckpointStatusDTO";
import SimpleCheckpointStatusDTO from "src/models/frontend/redux/SimpleCheckpointStatusDTO";
import CheckpointStatusDTO from "src/models/frontend/redux/CheckpointStatusDTO";
import FullUserStatusDTO from "src/models/user/FullUserStatusDTO";
import DebugUtil from "src/utils/DebugUtil";
import { SetPreviousLocationConfigAction } from "src/redux/ConfigActions";

export interface SetUserCheckpointAction extends BaseAction { type: "SET_CHECKPOINT"; data: FullCheckpointStatusDTO; }
export interface UpdateUserCheckpointAction extends BaseAction { type: "UPDATE_CHECKPOINT"; data: FullCheckpointStatusDTO; }
export interface UpdateStateCheckpointAction extends BaseAction { type: "UPDATE_STATE_CHECKPOINT"; data: KnownCheckpointStates; }
export interface EnableReplayCheckpointAction extends BaseAction { type: "ENABLE_REPLAY_CHECKPOINT"; data: { start: CheckpointStatusDTO, end: CheckpointStatusDTO }; }
export interface DisableReplayCheckpointAction extends BaseAction { type: "DISABLE_REPLAY_CHECKPOINT"; }
export interface ResetCheckpointAction extends BaseAction { type: "RESET_CHECKPOINT"; }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownAction = SetUserCheckpointAction | UpdateUserCheckpointAction | UpdateStateCheckpointAction | ResetCheckpointAction | EnableReplayCheckpointAction | DisableReplayCheckpointAction;

/** Used in the UserReducer */
export type KnownCheckpointStates = "none" | "loading" | "fresh" | "not-fresh";

export default class CheckpointAction {
    constructor() {
        // Dont be that guy
        throw new Error("NOOOO");
    }

    public static LoadUserStatus(dispatch: Dispatch<ReduxStoreModel>): Promise<boolean> {
        dispatch({ type: "UPDATE_STATE_CHECKPOINT", data: "loading" } as UpdateStateCheckpointAction);

        return UserApiController.getFullUserStatus().then(result => {
            if (!result.ok) {
                dispatch({ type: "UPDATE_STATE_CHECKPOINT", data: "none" } as UpdateStateCheckpointAction);
                return false;
            }

            let data = this.UserStatusToCheckPointStatus(result.data);
            data.currentDirection = "forwards";

            dispatch({ type: "SET_CHECKPOINT", data: data } as SetUserCheckpointAction);
            dispatch({ type: "UPDATE_STATE_CHECKPOINT", data: "fresh" } as UpdateStateCheckpointAction);
            return true;
        });
    }

    /**
     * Updates the database with the new current location. Updates furthest as needed, previous is ***not*** affected
     */
    public static UpdateCurrentUserStatus(dispatch: Dispatch<ReduxStoreModel>, request: SimpleCheckpointStatusDTO) {
        if (request == null) {
            return;
        }

        return dispatch((innerDispatch: Dispatch<ReduxStoreModel>, reduxState: () => ReduxStoreModel) => {
            let state = reduxState();

            // Patch for replay code start
            if (state.Mission.isReplayModeActive) {
                console.warn("% Replay mode Active %"); // JB - Remove this console.warn
                // Check if we are at the 'end' and set location accordingly
                let replay = state.Mission.replay;
                if (request.planetId >= replay.planetId && request.checkpointId >= replay.checkpointId && request.stepId >= replay.stepId) {
                    return this.EndReplay(innerDispatch);
                }

                let data = {
                    current: {
                        planetId: request.planetId,
                        checkpointId: request.checkpointId,
                        stepId: request.stepId
                    },
                    currentDirection: request.currentDirection
                } as FullCheckpointStatusDTO;
                return innerDispatch({ type: "UPDATE_CHECKPOINT", data: data } as UpdateUserCheckpointAction);
            }
            // End Patch

            UserApiController.updateUserStatus(request).then(result => {
                let convertedResult = this.UserStatusToCheckPointStatus(result.data);
                convertedResult.currentDirection = request.currentDirection;

                innerDispatch({ type: "UPDATE_CHECKPOINT", data: convertedResult } as UpdateUserCheckpointAction);
                if (DebugUtil.isDebugEnabled()) {
                    console.warn("Updated User Status in DB");
                }
            });
        });
    }

    /**
     * Enables replay mode and changes the 'Current' variable to the request. This value is then stored in 'Previous' and can be undone with the EndReplay() call
     */
    public static StartReplay(dispatch: Dispatch<ReduxStoreModel>, start: CheckpointStatusDTO, end: CheckpointStatusDTO, previousLocation: string) {
        if (start == null || end == null) {
            return;
        }

        dispatch({ type: "UPDATE_STATE_CHECKPOINT", data: "not-fresh" } as UpdateStateCheckpointAction);
        dispatch({ type: "SET_PREVIOUS_LOCATION_CONFIG", data: previousLocation } as SetPreviousLocationConfigAction);
        dispatch({ type: "ENABLE_REPLAY_CHECKPOINT", data: { start: start, end: end } } as EnableReplayCheckpointAction);
    }

    /**
     * Disables replay mode and resets 'Current' to the 'Previous' value set when call from StartReplay()
     */
    public static EndReplay(dispatch: Dispatch<ReduxStoreModel>) {
        return dispatch({ type: "DISABLE_REPLAY_CHECKPOINT" } as DisableReplayCheckpointAction);
    }

    private static UserStatusToCheckPointStatus(input: FullUserStatusDTO): FullCheckpointStatusDTO {
        if (input == null || input.checkpointId === 0 || input.planetId === 0) {
            // Data is either new or malformed
            let newStatus = {
                checkpointId: Checkpoints.Onboarding.id,
                planetId: Planets.R.id,
                stepId: 1,
            };
            return {
                // Use the expansion operator to ensure we have unique objects
                current: { ...newStatus },
                furthest: { ...newStatus }, // Since we dont know, just set the furthest to the current
                previous: null, // By design
                replay: null,
                currentDirection: "forwards"
            };
        }
        return {
            current: {
                planetId: input.planetId,
                checkpointId: input.checkpointId,
                stepId: input.stepId,
            },
            furthest: {
                planetId: input.furthestPlanetId,
                checkpointId: input.furthestCheckpointId,
                stepId: input.furthestStepId,
            },
            previous: null, // By design
            replay: null,
            currentDirection: "forwards"
        };
    }

    /**
     * Does not update the database
     */
    public static SetUserStatus(dispatch: Dispatch<ReduxStoreModel>, request: FullCheckpointStatusDTO) {
        if (request == null) {
            return;
        }

        dispatch({ type: "SET_CHECKPOINT", data: request } as SetUserCheckpointAction);
        dispatch({ type: "UPDATE_STATE_CHECKPOINT", data: "fresh" } as UpdateStateCheckpointAction);
    }

    public static SetFreshnessStatus(dispatch: Dispatch<ReduxStoreModel>, request: KnownCheckpointStates) {
        if (request == null) {
            return;
        }

        dispatch({ type: "UPDATE_STATE_CHECKPOINT", data: request } as UpdateStateCheckpointAction);
    }

    public static Logout(dispatch: Dispatch<ReduxStoreModel>) {
        dispatch({ type: "RESET_CHECKPOINT" } as ResetCheckpointAction);
    }

    public static Debug_SetCheckpoint(dispatch: Dispatch<ReduxStoreModel>, request: FullCheckpointStatusDTO) {
        dispatch({ type: "SET_CHECKPOINT", data: request } as SetUserCheckpointAction);
        dispatch({ type: "UPDATE_STATE_CHECKPOINT", data: "not-fresh" } as UpdateStateCheckpointAction);
    }
}



// WEBPACK FOOTER //
// ./src/redux/CheckpointActions.ts